/* eslint-disable no-param-reassign */
import { useState, useEffect, useRef, useMemo } from 'react'
import { trackWindowScroll } from 'react-lazy-load-image-component'
import withStyles from '@material-ui/core/styles/withStyles'
import map from 'ramda/src/map'
import splitEvery from 'ramda/src/splitEvery'
import keys from 'ramda/src/keys'
import forEach from 'ramda/src/forEach'
import Typography, { typographyVariants } from 'root/src/client/web/typography/Typography'
import DareCardDrop from 'root/src/client/web/list/listItemComponents/dare/DareCardDrop'
import {
	ltGtMediaQuery,
	gtLgMediaQuery,
	gtSmMediaQuery,
} from 'root/src/client/web/commonStyles'
import anchors from 'root/src/shared/constants/anchors'
import { DaresLeft } from 'root/src/client/web/record/DropEvent/DaresLeft'
import { DropEventDarePlaceholder } from 'root/src/client/web/record/DropEvent/placeholders/DareCardPlaceholder'
import { useIsMobileResolution } from 'root/src/client/v2/browser/hooks/useIsMobileResolution'
import { getDropDarePlatforms } from 'root/src/shared/drops/helpers/dropDare.helpers'
import dynamic from 'next/dynamic'

import MaxWidthContainer from 'root/src/client/web/base/MaxWidthContainer'
import { DaresFilters } from 'root/src/client/web/record/DropEvent/DaresFilter/DaresFilters'
import { DaresFiltersProvider } from 'root/src/client/web/record/DropEvent/DaresFilter/DaresFilterProvider'
import { getReadableDarePlatform } from 'root/src/shared/platforms/helpers/getReadablePlatform'
import { dropDareDifficultyLevelEnum, readableDropDareDifficultyLevels } from 'root/src/shared/constants/dropEvent'
import { twMerge } from 'tailwind-merge'
import { uniqBy } from 'remeda'
import { useOnboardingQueryParam } from 'root/src/client/v2/onboarding/hooks/useOnboardingQueryParam'
import { DarePlatform } from 'root/src/shared/@types/platforms'

const Pager = dynamic(() => import('root/src/client/web/base/Pager'), { ssr: false })

const DaresSectionComingSoonPlaceholder = () => (
	<>
		<DropEventDarePlaceholder />
		<DropEventDarePlaceholder />
		<DropEventDarePlaceholder />
	</>
)

const style = {
	daresSection: {
		position: 'relative',
		margin: [[50, 'auto']],
		width: '100%',
		overflow: 'hidden',
		[ltGtMediaQuery]: {
			width: '100%',
		},
		[gtLgMediaQuery]: {
			width: '100%',
		},
		[gtSmMediaQuery]: {
		},
	},
	daresHeadingContainer: {
		margin: [[22, 'auto']],
		[ltGtMediaQuery]: {
			textAlign: 'center',
		},
	},
	widgetsContainer: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
	},
	daresContainer: {
		width: '100%',
		display: 'grid',
		marginTop: 30,
		transform: 'translateX(100%)',
		gridTemplateColumns: 'repeat(3, 1fr)',
		justifyItems: 'center',
		height: 960,

		'@media (max-width: 1100px)': {
			gridTemplateColumns: 'repeat(2, 1fr)',
			height: 1440,
		},
		'@media (max-width: 650px)': {
			gridTemplateColumns: '1fr',
			height: 'auto',
		},
	},
	card: {
		width: 'fit-content',
		margin: [[20, 0]],
		transitionDuration: '.3s',
	},
	maxDaresText: {
		textAlign: 'center',
		margin: [[30, 0]],
	},
}

const setStyles = (element, styles) => {
	forEach((property) => {
		if (!element) {
			return
		}
		element.style[property] = styles[property]
	},
	keys(styles))
}
const setNestedStyles = (ref, styles) => forEach(
	child => setStyles(child, styles),
	ref.current?.children,
)

const animationActions = {
	page: 'page',
	filter: 'filter',
}

export const getDaresAvailableFilters = (dares) => {
	const getAvailablePlatforms = () => {
		const options = Object.values(DarePlatform)
			.map(darePlatform => ({
				value: darePlatform,
				label: getReadableDarePlatform(darePlatform),
			}))
		const platforms = getDropDarePlatforms(dares)
		return options.filter(({ value }) => platforms.includes(value))
	}
	const getAvailableLevels = () => Object.values(dropDareDifficultyLevelEnum)
		.map(level => ({
			value: level,
			label: readableDropDareDifficultyLevels[level],
		}))
		.filter(option => dares.some(dare => option.value === dare.level))

	const getAvailableTiers = () => {
		const dareTiers = dares.map(dare => dare.battlePass?.tier)
		const tiers = Array.from(new Set(dareTiers))
		return tiers
			.filter(Boolean)
			.map(tier => ({
				value: tier,
				label: `Tier ${tier}`,
			}))
	}

	const getAvailableBattlePasses = () => {
		const filteredDares = uniqBy(dares.filter(dare => dare.battlePass), dare => dare.battlePass.id)
		return filteredDares.map(dare => ({
			value: dare.battlePass.id,
			label: dare.battlePass.name,
		}))
	}

	return {
		levels: getAvailableLevels(),
		platforms: getAvailablePlatforms(),
		tiers: getAvailableTiers(),
		battlePasses: getAvailableBattlePasses(),
	}
}

const DaresSection = ({
	classes, dares, gameName, content, canAccept, campaignId, submitting, setSubmitting,
	emailSubscription, toggleEmailSubscription, comingSoon, canAccessCreateBrandProject,
	showTermsModal, pointsToEarn, canEarnPoints, isAbleToParticipate, refetchDropEvent, drop,
}) => {
	const itemsAmount = 6
	const { primaryHeading, secondaryHeading } = content
	const [currentPage, setCurrentPage] = useState(1)
	const [allPage, setAllPage] = useState(Math.ceil(dares.length / itemsAmount))
	const [pageItems, setPageItems] = useState(splitEvery(itemsAmount, dares)[0] || [])
	const wrapperRef = useRef()
	const actionTypeRef = useRef(null)
	const { isMobileResolution } = useIsMobileResolution()
	const isPlaceholder = !dares.length
	const [isOnboarding] = useOnboardingQueryParam()

	const filterOptions = useMemo(() => getDaresAvailableFilters(dares), [dares])

	const [filterState, setFilterState] = useState({
		platforms: [],
		levels: [],
		tiers: [],
		battlePasses: [],
	})

	const filterDares = () => {
		const newDares = dares.filter((dare) => {
			if (filterState.platforms.length && !filterState.platforms.some(platform => dare.platforms.includes(platform))) {
				return false
			}
			if (filterState.levels.length && !filterState.levels.includes(dare.level)) {
				return false
			}
			if (dare.battlePass) {
				if (filterState.battlePasses.length && !filterState.battlePasses.includes(dare.battlePass.id)) {
					return false
				}
				if (filterState.tiers.length && !filterState.tiers.includes(dare.battlePass.tier)) {
					return false
				}
			}
			return true
		})
		const currentPageIndex = currentPage - 1

		setAllPage(Math.ceil(newDares.length / itemsAmount) || 1)
		setPageItems(splitEvery(itemsAmount, newDares)[currentPageIndex] || [])
	}

	const disappearingAnimation = () => {
		setNestedStyles(wrapperRef, {
			transitionDuration: '.3s',
			transitionTimingFunction: 'cubic-bezier(.27,-0.48,1,.81)',
			transform: 'scale(0)',
		})
		setTimeout(() => {
			setStyles(wrapperRef.current, {
				transitionDuration: '0s',
				transform: 'translateX(100%)',
			})
		}, 300)
	}

	const appearingAnimation = () => {
		setTimeout(() => {
			filterDares()
			setNestedStyles(wrapperRef, {
				transitionDuration: '0s',
				transform: 'scale(1)',
			})
			setStyles(wrapperRef.current, {
				transitionDuration: '.2s',
				transitionTimingFunction: 'ease-in',
				transform: 'translateX(0)',
			})
		}, 320)
	}

	const onFilterChange = (state) => {
		setFilterState(state)
		setCurrentPage(1)
	}

	useEffect(() => {
		filterDares()
	}, [dares])

	useEffect(() => {
		disappearingAnimation()
		if (actionTypeRef.current === animationActions.filter) {
			if (currentPage === 1) {
				appearingAnimation()
			} else {
				setCurrentPage(1)
			}
		} else {
			appearingAnimation()
		}
		actionTypeRef.current = null
	}, [currentPage, filterState])

	return (
		<DaresFiltersProvider
			drop={drop}
			onFilterChange={onFilterChange}
			value={filterState}
			options={filterOptions}
		>
			<div className="relative flex">
				<div
					id={anchors.DROP_ALL_DARES}
					className={classes.daresSection}
				>
					<MaxWidthContainer>
						<div className="w-full">
							<div className={classes.daresHeadingContainer}>
								<Typography variant={typographyVariants.heading3}>{primaryHeading}</Typography>
								<Typography variant={typographyVariants.heading2}>{secondaryHeading(gameName)}</Typography>
							</div>
							<div className="flex pb-8 pt-2 justify-center items-center">
								<DaresLeft />
							</div>
							<div className={classes.widgetsContainer}>
								<Pager
									currentPage={currentPage}
									allPage={allPage}
									getPage={(newPage) => {
										actionTypeRef.current = animationActions.page
										setCurrentPage(newPage)
									}}
								/>
							</div>
						</div>
					</MaxWidthContainer>
					<div className="flex gap-8">
						<DaresFilters />
						<MaxWidthContainer>
							<div
								ref={wrapperRef}
								className={twMerge(classes.daresContainer, 'gap-4')}
							>
								{isPlaceholder ? (
									<DaresSectionComingSoonPlaceholder />
								) : (
									<>
										{
											map(item => (
												<DareCardDrop
													refetchDropEvent={refetchDropEvent}
													className={classes.card}
													dareCardClassName={twMerge('mx-auto mb-auto')}
													tooltipClassName={twMerge('!items-start self-start', isOnboarding && 'first:rounded-3xl first:border-[6px] first:border-teal')}
													hover={item.prizeAvailable && !item.userCreated}
													dare={item}
													canAccept={canAccept}
													campaignId={campaignId}
													submitting={submitting}
													setSubmitting={setSubmitting}
													key={item.title + item.description}
													comingSoon={comingSoon}
													emailSubscription={emailSubscription}
													toggleEmailSubscription={toggleEmailSubscription}
													canAccessCreateBrandProject={canAccessCreateBrandProject}
													showTermsModal={showTermsModal}
													pointsToEarn={pointsToEarn}
													canEarnPoints={canEarnPoints}
													isAbleToParticipate={isAbleToParticipate}
												/>
											), pageItems)
										}
									</>
								)}
							</div>
						</MaxWidthContainer>
					</div>
					{!isMobileResolution && (
						<MaxWidthContainer>
							<Pager
								className="scale-150 mt-6"
								currentPage={currentPage}
								allPage={allPage}
								getPage={(newPage) => {
									actionTypeRef.current = animationActions.page
									setCurrentPage(newPage)
								}}
							/>

						</MaxWidthContainer>
					)}
				</div>
			</div>
		</DaresFiltersProvider>
	)
}

export default withStyles(style, { name: 'MuiDaresSection' })(trackWindowScroll(DaresSection))
